import API from '@/utils/api/common';
import { apiService } from '@/utils/api/index';
import router from '@/router/dashboard.js';
import {
  genericError,
  genericSuccess,
  emailError,
  inviteSuccess,
} from '@/utils/alerts.service';

export const userAccount = {
  namespaced: true,
  state: {
    data: {
      firstName: '',
      lastName: '',
    },
    api: {
      token: null,
    },
    firstNameError: false,
    lastNameError: false,
    oldPassword: '',
    oldPasswordError: '',
    password: '',
    passwordError: false,
    confirmPassword: '',
    confirmPasswordError: false,
    dirty: [],
    loading: false,
  },
  getters: {
    userInfo(state) {
      return state;
    },
    error(state) {
      if (state.firstNameError || state.lastNameError) return true;
      if (state.oldPasswordError || state.confirmPasswordError) return true;
      if (state.passwordError) return true;
      return false;
    },
    loading(state) {
      return state.loading;
    },
  },
  mutations: {
    updateUserName(state, payload) {
      return (state.data[payload.key] = payload.value);
    },

    updateUserPassword(state, payload) {
      return (state[payload.key] = payload.value);
    },

    setUser(state, data) {
      state.data = data;
    },

    setApi(state, data) {
      state.api = data;
    },

    markDirtyTab(state, tab) {
      state.dirty.push(tab);
    },

    markNameError(state, payload) {
      if (!payload.value) {
        state[payload.key + 'Error'] = 'This field cannot be blank';
      } else {
        state[payload.key + 'Error'] = false;
      }
    },

    markPasswordError(state, payload) {
      if (!payload.value) {
        return (state[payload.key + 'Error'] = 'This field cannot be blank');
      }

      if (
        payload.key === 'confirmPassword' &&
        payload.value !== state.password
      ) {
        return (state[payload.key + 'Error'] = 'Passwords do not match!');
      }
      return (state[payload.key + 'Error'] = false);
    },

    setLoading(state, value) {
      state.loading = value;
    },
  },
  actions: {
    fetchUser({ commit, dispatch }) {
      return API.getUserInfo()
        .then(data => {
          commit('setUser', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    updateUserName({ commit }, payload) {
      return commit('updateUserName', payload);
    },

    updateUserPassword({ commit }, payload) {
      return commit('updateUserPassword', payload);
    },

    async fetchApiKey({ state, commit, dispatch }) {
      if (!state.data.id) {
        await dispatch('fetchUser');
      }
      const user = state.data.id;
      return API.getApiKey(user)
        .then(data => {
          commit('setApi', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async generateApiKey({ state, commit, dispatch }) {
      if (!state.data.id) {
        await dispatch('fetchUser');
      }
      const user = state.data.id;
      return API.generateApiKey(user)
        .then(res => {
          commit('setApi', res.data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async disableApiKey({ state, commit, dispatch }) {
      if (!state.data.id) {
        await dispatch('fetchUser');
      }
      const user = state.data.id;
      return API.disableApiKey(user)
        .then(data => {
          commit('setApi', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    markDirty({ commit, state }, tab) {
      if (!state.dirty.includes(tab)) {
        return commit('markDirtyTab', tab);
      }
      return;
    },

    markNameError({ commit }, payload) {
      commit('markNameError', payload);
    },

    markPasswordError({ commit }, payload) {
      commit('markPasswordError', payload);
    },

    saveChanges({ state, dispatch }) {
      if (state.dirty.includes('name')) dispatch('saveName');
      if (state.dirty.includes('password')) dispatch('savePassword');
    },

    saveName({ dispatch, state, commit }) {
      const data = {
        firstName: state.data.firstName,
        lastName: state.data.lastName,
        email: state.data.email,
      };
      commit('setLoading', true);
      return API.saveUserInfo(data)
        .then(res => {
          dispatch('showUserMessage', { ...genericSuccess }, { root: true });
          dispatch('setUser', res.data, { root: true });
          commit('setLoading', false);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
          commit('setLoading', false);
        });
    },

    savePassword({ dispatch, state, commit }) {
      const data = {
        oldPassword: state.oldPassword,
        newPassword: state.password,
      };
      commit('setLoading', true);
      return API.saveUserPassword(data)
        .then(data => {
          localStorage.setItem('access_token', data.data.accessToken);
          apiService.setHeader();
          dispatch('showUserMessage', { ...genericSuccess }, { root: true });
          commit('setLoading', false);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
          commit('setLoading', false);
        });
    },

    /*
      User management
    */
    sendInvites({ dispatch }, invites) {
      let success = 0;
      for (let invite of invites) {
        const data = {
          name: invite.name,
          email: invite.email,
        };

        API.sendUserInvite(data)
          .then(response => {
            success++;
            if (response.status == 400) {
              var errorMsg = 'Something went wrong.';
              if (response.data.message != undefined) {
                errorMsg = response.data.message;
              } else if (response.data.email != undefined) {
                errorMsg = 'You already invited someone using this email.';
              }
              dispatch(
                'showUserMessage',
                {
                  success: false,
                  title: 'Oops!',
                  message: errorMsg,
                },
                { root: true }
              );
            } else {
              dispatch('showUserMessage', { ...inviteSuccess }, { root: true });
            }
            if (success === invites.length) {
              router.push('/account/team');
            }
          })
          .catch(e => {
            var errorMsg = 'Something went wrong.';
            if (e.data.message != undefined) {
              errorMsg = e.data.message;
            } else if (e.data.email != undefined) {
              errorMsg = 'You already invited someone using this email.';
            }
            try {
              dispatch(
                'showUserMessage',
                {
                  success: false,
                  title: 'Oops!',
                  message: errorMsg,
                },
                { root: true }
              );
            } catch {
              dispatch('showUserMessage', { ...emailError }, { root: true });
            }
          });
      }
    },
  },
};
